"use client";

import { memo, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Typography } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import Icon from "@/components/ui/Icon";
import { useCheckoutStore } from "@/store/useCheckoutStore";
import { useStartOrderConfirmedQueryData } from "@/hooks/useStartOrderConfirmedQueryData";
import { useGTMPurchaseEvent } from "@/hooks/useGTMPurchaseEvent";
import AccountCreationCard from "./AccountCreationCard";
import CompactOrderSummary from "./CompactOrderSummary";

// PROD-2026-05-18-011 Layout 2 — "linear stack".
//
//  ┌──────────────────────────────────────────┐
//  │  ✓ Personalised success header           │  <- top of fold
//  │  Order #, receipt sent, delivery date    │
//  └──────────────────────────────────────────┘
//  ┌──────────────────────────────────────────┐
//  │  Account creation (full form, prefilled) │  <- peak attention
//  └──────────────────────────────────────────┘
//  ┌──────────────────────────────────────────┐
//  │  Order details (compact, validating)     │  <- below fold ok
//  └──────────────────────────────────────────┘
//                  [Go to homepage]
//
// CRO rationale: customer reads success line → eye lands on account
// block at peak post-purchase attention → order details still
// available as the validating section once they've made the account
// decision. Single primary CTA per block; same mobile and desktop
// behaviour because the layout is a clean vertical stack.

export type ConfirmationLayoutV2Props = {
  orderId: string | null;
};

const ConfirmationLayoutV2 = ({ orderId }: ConfirmationLayoutV2Props) => {
  const router = useRouter();
  const { cleanUp } = useCheckoutStore();
  const { firstName, lastName, email } = useStartOrderConfirmedQueryData();
  useGTMPurchaseEvent();

  useEffect(() => {
    cleanUp();
  }, []);

  const greetingName = firstName?.trim();

  return (
    <div className="mx-auto w-full max-w-[800] grid gap-4">
      {/*
        Compact success header per r6 spec. Earlier r4/r5 versions
        used a tall, brochure-style "trophy" card (h-16 icon,
        py-8 md, big inter-element margins) which by itself ate
        ~190px of vertical real estate before the account card even
        rendered — pushing the primary CTA below the fold on a
        16-inch MacBook. New treatment keeps all the same content
        (icon, greeting, order#, receipt note) but on a single
        consolidated row on desktop and tighter padding on mobile.
        Net saving: ~80px vertical, enough to land "Access My
        Dashboard" above the fold without touching the account
        card's own height budget.
      */}
      <div className="rounded-[18px] bg-white border border-border-default shadow-[0_3.6px_45.04px_0px_rgba(0,0,0,0.04)] px-4 py-4 md:px-6 md:py-5">
        <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:gap-4 md:text-left">
          <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-100">
            <Icon
              id="icon-check_20"
              width={28}
              height={28}
              className="text-green-500"
            />
          </span>
          <div className="grid gap-0.5 min-w-0">
            <Typography
              variant="titleSm"
              className="font-semibold text-semantic-text-primary md:text-[18px] md:leading-[130%]"
              asChild
            >
              <h2>
                {greetingName
                  ? `Thank you, ${greetingName} — your order is confirmed!`
                  : "Thank you, your order is confirmed!"}
              </h2>
            </Typography>
            {/*
              Mobile fix r8: subline now uses flex-wrap so long
              emails or order IDs don't overflow on narrow phones
              (iPhone SE/Mini at 375px). On wide screens the two
              segments still sit inline with a dot separator; on
              narrow screens the dot becomes a soft break point and
              "Receipt sent to ..." wraps to its own line. Email
              gets break-all because addresses have no natural
              word-break points and would otherwise blow out the
              container width.
            */}
            <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[13px] font-medium text-semantic-text-secondary md:justify-start">
              {orderId && (
                <span className="text-semantic-text-primary font-semibold">
                  Order #{orderId}
                </span>
              )}
              {orderId && email && (
                <span aria-hidden="true" className="hidden sm:inline">
                  ·
                </span>
              )}
              {email && (
                <span className="inline-flex flex-wrap items-baseline gap-x-1">
                  <span>Receipt sent to</span>
                  <span className="text-semantic-text-primary font-medium break-all">
                    {email}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Account creation — peak attention */}
      <AccountCreationCard
        firstName={firstName}
        lastName={lastName}
        email={email}
        orderId={orderId}
      />

      {/*
        Order details collapsed to a single tappable line by default
        per r4 test-pass feedback. Customer also gets the breakdown
        in the confirmation email so hiding it here doesn't hurt
        access — and it lets the account block hold the visual
        hierarchy without the long detailed card competing for
        attention right below it.
      */}
      <CompactOrderSummary variant="collapsed" />

      {/* Secondary CTA */}
      <div>
        <Button
          onClick={() => router.push("/")}
          type="button"
          variant="outline"
          className="w-full h-12"
        >
          Go to homepage
        </Button>
      </div>
    </div>
  );
};

export default memo(ConfirmationLayoutV2);
